import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildLoadExecutionDetailPayload,
  fetchLoadExecutionDetail,
  LoadExecutionDetailData,
  LoadExecutionDetailRecord,
  AppliedFiltersForLoadExecution,
} from '../services/onTimeDispatchLoadExecutionApi';

export interface UseLoadExecutionDetailReturn {
  records: LoadExecutionDetailRecord[];
  loading: boolean;
  error: string | null;
  totalElements: number;
  pageNumber: number;
  pageSize: number;
  sortBy: string | undefined;
  sortDirection: 'ASC' | 'DESC' | undefined;
  setPageNumber: (page: number) => void;
  setPageSize: (size: number) => void;
  setSorting: (sortBy: string | null, sortDirection: 'ASC' | 'DESC' | null) => void;
  refresh: () => void;
}

/**
 * Hook to fetch Load Execution detail records based on applied filters
 * Handles pagination, sorting, and filter-based data fetching
 *
 * ✅ Fetch pattern intentionally mirrors useLocationSummaryApi.ts (the
 * Location Summary tab's data hook), which reliably updates its table on
 * every sort/page/filter change: build a single memoized request `payload`
 * from ALL of the inputs that should trigger a refetch (filters, page,
 * size, sortBy, sortDirection), then run exactly one plain (non-debounced)
 * useEffect keyed on that payload that fetches and applies the result,
 * guarded only by a per-invocation local `isMounted` flag.
 *
 * The previous implementation used a separate debounce timer *plus* a
 * global monotonically-increasing "request sequence" ref shared across all
 * fetches to detect/discard stale out-of-order responses. That extra layer
 * of indirection made it possible for a sort-triggered refetch to have its
 * *own* debounce timer torn down/rescheduled relative to an already
 * in-flight filter/page fetch in ways that could leave the table not
 * reflecting the most recent sorted response. Using one memoized payload +
 * one effect removes that class of bug entirely: React's effect
 * cleanup/re-run semantics guarantee only the fetch tied to the *current*
 * payload can ever update state, exactly like Location Summary.
 *
 * @param appliedFilters Current applied filters snapshot
 * @param isActive Whether the Load Execution tab is active
 * @param adminLocations Full list of available locations for ID resolution
 * @returns Hook interface with records, loading, error, and control functions
 */
export function useLoadExecutionDetail(
  appliedFilters: AppliedFiltersForLoadExecution,
  isActive: boolean,
  adminLocations: any[]
): UseLoadExecutionDetailReturn {
  const [records, setRecords] = useState<LoadExecutionDetailRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [pageNumber, setPageNumberState] = useState(1);
  const [pageSize, setPageSizeState] = useState(25);
  const [sortBy, setSortBy] = useState<string | undefined>('manifestArrivalDateTime');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | undefined>('ASC');
  const [triggerCount, setTriggerCount] = useState(0);

  // Reset to page 1 and clear previously loaded rows whenever the filter
  // payload changes (e.g. a new location/region/market/date range is
  // selected) — mirrors LocationSummaryTab's `prevLocationSummaryFiltersKeyRef`
  // pattern (see LocationSummaryTab.tsx). Doing this as a render-time reset
  // (rather than inside a useEffect) means the very next render already has
  // pageNumber back at 1 *before* the fetch effect below runs, so there is
  // only ever one fetch per filter change instead of one for the stale page
  // followed immediately by another for the reset page.
  const filtersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);
  const prevFiltersKeyRef = useRef(filtersKey);
  if (prevFiltersKeyRef.current !== filtersKey) {
    prevFiltersKeyRef.current = filtersKey;
    if (pageNumber !== 1) {
      setPageNumberState(1);
    }
    if (records.length > 0) {
      setRecords([]);
    }
    if (totalElements !== 0) {
      setTotalElements(0);
    }
    if (error !== null) {
      setError(null);
    }
  }

  // Single source of truth for "what should currently be fetched". Sorting
  // and pagination are just additional fields on this same payload, exactly
  // like useLocationSummaryApi's `payload` memo — this is *why* re-sorting
  // there reliably triggers a fresh, correctly-ordered fetch, and is the
  // pattern being replicated here.
  const payload = useMemo(
    () =>
      adminLocations && adminLocations.length > 0
        ? buildLoadExecutionDetailPayload(
            appliedFilters,
            adminLocations,
            pageNumber,
            pageSize,
            sortBy,
            sortDirection
          )
        : null,
    [appliedFilters, adminLocations, pageNumber, pageSize, sortBy, sortDirection]
  );

  useEffect(() => {
    if (!isActive) {
      console.log('[Load Execution Detail Hook] Tab not active, skipping fetch');
      return;
    }

    // ✅ Data readiness check - only proceed when adminLocations is fully
    // loaded. This prevents a race condition where the API call happens
    // before location data is available (payload is null until then).
    if (!payload) {
      console.log('[Load Execution Detail Hook] Waiting for adminLocations to load (current length:', adminLocations?.length || 0, ')');
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (payload.locationId) {
          console.log(
            '[Load Execution Detail Hook] Request includes',
            payload.locationId.length,
            'location IDs:',
            payload.locationId
          );
        }
        console.log('[Load Execution Detail Hook] Fetching detail records with request:', JSON.stringify(payload, null, 2));
        const data: LoadExecutionDetailData = await fetchLoadExecutionDetail(payload);
        if (isMounted) {
          setRecords(data.records);
          setTotalElements(data.totalElements);
          setError(null);
          console.log('[Load Execution Detail Hook] Detail records loaded successfully:', data);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch detail records';
          setError(errorMessage);
          setRecords([]);
          setTotalElements(0);
          console.error('[Load Execution Detail Hook] Detail fetch failed:', errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [payload, isActive, triggerCount]);

  const setSorting = useCallback(
    (newSortBy: string | null, newSortDirection: 'ASC' | 'DESC' | null) => {
      console.log('[Load Execution Detail Hook] Setting sort:', newSortBy, newSortDirection);
      // Normalize null -> undefined so downstream consumers (which type
      // sortBy as `string | undefined`) always see a consistent "no sort"
      // value instead of `null` leaking through from the reset (3rd click)
      // case in LoadExecution.tsx.
      setSortBy(newSortBy ?? undefined);
      setSortDirection(newSortDirection ?? undefined);
      setPageNumberState(1);
    },
    []
  );

  const handleSetPageNumber = useCallback((newPage: number) => {
    console.log('[Load Execution Detail Hook] Setting page:', newPage);
    setPageNumberState(Math.max(1, newPage));
  }, []);

  const handleSetPageSize = useCallback((newSize: number) => {
    console.log('[Load Execution Detail Hook] Setting page size:', newSize);
    setPageSizeState(Math.max(1, newSize));
    setPageNumberState(1);
  }, []);

  const refresh = useCallback(() => {
    console.log('[Load Execution Detail Hook] Triggering manual refresh');
    setTriggerCount((prev) => prev + 1);
  }, []);

  return {
    records,
    loading,
    error,
    totalElements,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    setPageNumber: handleSetPageNumber,
    setPageSize: handleSetPageSize,
    setSorting,
    refresh,
  };
}
